import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Users, User, Clock, BookOpen, GraduationCap,
  RefreshCw, X, Loader2, Plus, UserCheck, ChevronRight,
  GitMerge, Check, Search,
} from 'lucide-react'
import { classesApi, studentsApi, teachersApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'

// ─── Academic Year Utils ───────────────────────────────────────────────────────

const ACADEMIC_START_MONTH = 3 // April (0-indexed)

function getCurrentAcademicYear() {
  const d = new Date()
  const y = d.getMonth() >= ACADEMIC_START_MONTH ? d.getFullYear() : d.getFullYear() - 1
  return `${y}-${y + 1}`
}

function getNextAcademicYear(value) {
  const m = /^(\d{4})-\d{4}$/.exec(value)
  if (!m) return value
  const n = Number(m[1]) + 1
  return `${n}-${n + 1}`
}

function getDefaultPromotionYear() {
  const d = new Date()
  const cur = getCurrentAcademicYear()
  return d.getMonth() < ACADEMIC_START_MONTH ? getNextAcademicYear(cur) : cur
}

function isValidAcademicYear(value) {
  const m = /^(\d{4})-(\d{4})$/.exec((value || '').trim())
  return !!m && Number(m[2]) === Number(m[1]) + 1
}

function getAcademicYearOptions() {
  const start = Number(getCurrentAcademicYear().slice(0, 4))
  const def = getDefaultPromotionYear()
  const years = Array.from({ length: 6 }, (_, i) => { const y = start - 2 + i; return `${y}-${y + 1}` })
  return [...new Set([...years, def])]
}

// ─── Class Progression Utils ──────────────────────────────────────────────────

const CLASS_LEVEL_MAP = {
  PRE_NURSERY: -4, PRE_KG: -3, NURSERY: -2, LKG: -1, LOWER_KG: -1,
  UKG: 0, UPPER_KG: 0, KG: 0, PREP: 0,
}

function getClassLevel(cls) {
  const name = String(cls?.name || '').trim().toUpperCase()
    .replace(/[.\-]/g, ' ').replace(/\s+/g, ' ').trim()
  if (!name) return null
  const stripped = name
    .replace(/^CLASS\s+/, '').replace(/^STD(?:ANDARD)?\s+/, '').replace(/^GRADE\s+/, '').trim()
  const key = stripped.replace(/\s+/g, '_')
  if (key in CLASS_LEVEL_MAP) return CLASS_LEVEL_MAP[key]
  const nm = stripped.match(/\b(\d{1,2})\b/)
  if (nm) return Number(nm[1])
  return null
}

function getSuggestedPromotionClasses(allClasses, currentClass) {
  const currentId = currentClass?._id || currentClass?.id
  const currentLevel = getClassLevel(currentClass)
  const others = allClasses.filter((c) => (c._id || c.id) !== currentId)

  if (currentLevel === null) return { orderedClasses: others, suggestedClasses: [], isHighestLevel: false }

  const higher = others.filter((c) => { const l = getClassLevel(c); return l !== null && l > currentLevel })
  if (!higher.length) return { orderedClasses: others, suggestedClasses: [], isHighestLevel: true }

  const nextLevel = Math.min(...higher.map((c) => getClassLevel(c)))
  const curSection = String(currentClass?.section || '').toUpperCase()
  const suggested = higher
    .filter((c) => getClassLevel(c) === nextLevel)
    .sort((a, b) => {
      const aM = String(a.section || '').toUpperCase() === curSection
      const bM = String(b.section || '').toUpperCase() === curSection
      if (aM !== bM) return aM ? -1 : 1
      return (a.section || '').localeCompare(b.section || '')
    })

  const suggestedIds = new Set(suggested.map((c) => c._id || c.id))
  return {
    orderedClasses: [...suggested, ...others.filter((c) => !suggestedIds.has(c._id || c.id))],
    suggestedClasses: suggested,
    isHighestLevel: false,
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getId = (u) => u?._id || u?.id || u
const getName = (u) => u?.name || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || '—'

const formatDate = (iso) =>
  iso ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(iso)) : '—'

const isTempActive = (tmp) => {
  if (!tmp?.teacherId) return false
  const today = new Date()
  return new Date(tmp.from) <= today && today <= new Date(tmp.to)
}

// ─── Temp Teacher Modal ───────────────────────────────────────────────────────

function TempTeacherModal({ teachers, onClose, onConfirm, loading }) {
  const [step, setStep] = useState('pick')
  const [selected, setSelected] = useState(null)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [search, setSearch] = useState('')

  const filtered = teachers.filter((t) =>
    getName(t).toLowerCase().includes(search.toLowerCase()) ||
    (t.email || '').toLowerCase().includes(search.toLowerCase())
  )

  const handleConfirm = () => {
    if (!from || !to) { toast.error('Select both from and to dates'); return }
    if (new Date(from) > new Date(to)) { toast.error('From date must be before to date'); return }
    onConfirm(getId(selected), from, to)
  }

  const today = new Date().toISOString().slice(0, 10)

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          {step === 'dates' ? (
            <button onClick={() => setStep('pick')} className="p-1 text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </button>
          ) : (
            <div className="w-7" />
          )}
          <h3 className="font-bold text-gray-900">
            {step === 'pick' ? 'Select Temp Teacher' : 'Set Date Range'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'pick' ? (
          <>
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <Search className="w-4 h-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search teacher..."
                  className="bg-transparent text-sm outline-none flex-1"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filtered.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-10">No teachers found</p>
              ) : filtered.map((t) => (
                <button
                  key={getId(t)}
                  onClick={() => { setSelected(t); setStep('dates') }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 text-left"
                >
                  <Avatar src={t.avatar} name={getName(t)} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900 truncate">{getName(t)}</p>
                    <p className="text-xs text-gray-500 truncate">{t.email || t.mobile || 'No contact'}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="p-5 space-y-4 flex-1 overflow-y-auto">
            <div className="flex items-center gap-2 bg-primary-50 border border-primary-200 rounded-xl px-4 py-3">
              <User className="w-4 h-4 text-primary-600 flex-shrink-0" />
              <span className="text-sm font-semibold text-primary-700">{getName(selected)}</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">From Date *</label>
              <input
                type="date"
                className="input-field w-full"
                value={from}
                min={today}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">To Date *</label>
              <input
                type="date"
                className="input-field w-full"
                value={to}
                min={from || today}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>

            <div className="flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
              <Clock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                The temp teacher can mark attendance only within this date range.
              </p>
            </div>

            <button
              onClick={handleConfirm}
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Assign Temp Teacher
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Promotion Modal ──────────────────────────────────────────────────────────

function PromotionModal({ currentClass, allClasses, students, onClose, onConfirm, loading }) {
  const defaultYear = getDefaultPromotionYear()
  const yearOptions = getAcademicYearOptions()

  const { orderedClasses, suggestedClasses, isHighestLevel } = useMemo(
    () => getSuggestedPromotionClasses(allClasses, currentClass),
    [allClasses, currentClass]
  )

  const [targetYear, setTargetYear] = useState(defaultYear)
  const [mode, setMode] = useState(
    isHighestLevel && suggestedClasses.length === 0 ? 'complete' : 'promote'
  )
  const [targetClassId, setTargetClassId] = useState(
    suggestedClasses[0]?._id || orderedClasses[0]?._id || ''
  )
  const [selectedIds, setSelectedIds] = useState(() =>
    students.map((s) => getId(s)).filter(Boolean)
  )

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const toggleStudent = (sid) =>
    setSelectedIds((prev) => prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid])

  const handleSubmit = () => {
    const year = targetYear.trim()
    if (!isValidAcademicYear(year)) { toast.error('Enter a valid academic year (e.g. 2025-2026)'); return }
    if (!selectedIds.length) { toast.error('Select at least one student'); return }
    if (mode === 'promote' && !targetClassId) { toast.error('Select a target class'); return }
    onConfirm({
      studentIds: selectedIds,
      targetAcademicYear: year,
      ...(mode === 'promote' ? { targetClassId } : { markCompleted: true }),
    })
  }

  const noSuggestionCopy = isHighestLevel
    ? 'No higher class detected. Complete this class for the next academic year, or choose another class manually.'
    : 'Pick the target class manually if the automatic suggestion does not match your school structure.'

  const currentLabel = `${currentClass?.name || ''}${currentClass?.section ? ` — ${currentClass.section}` : ''}`

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-900">Promote Students</h3>
            <p className="text-xs text-gray-500 mt-0.5">Academic progression for {currentLabel}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Academic Year */}
          <div className="card p-4 space-y-3">
            <h4 className="text-sm font-bold text-gray-900">Target Academic Year</h4>
            <input
              className="input-field w-full"
              value={targetYear}
              onChange={(e) => setTargetYear(e.target.value)}
              placeholder="e.g. 2025-2026"
            />
            <p className="text-xs text-gray-400">
              Any academic year can be typed above. Quick picks are just shortcuts.
            </p>
            <div className="flex flex-wrap gap-2">
              {yearOptions.map((y) => (
                <button
                  key={y}
                  onClick={() => setTargetYear(y)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    targetYear === y
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'border-gray-200 text-gray-600 hover:border-primary-300 hover:text-primary-700'
                  }`}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>

          {/* Mode */}
          <div className="card p-4 space-y-3">
            <h4 className="text-sm font-bold text-gray-900">Action</h4>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setMode('promote')}
                className={`p-3 rounded-xl border-2 text-sm font-semibold text-left transition-all ${
                  mode === 'promote'
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <GitMerge className="w-4 h-4 mb-1.5" />
                Promote to Class
              </button>
              <button
                onClick={() => setMode('complete')}
                className={`p-3 rounded-xl border-2 text-sm font-semibold text-left transition-all ${
                  mode === 'complete'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <GraduationCap className="w-4 h-4 mb-1.5" />
                Complete Highest Class
              </button>
            </div>
            <p className="text-xs text-gray-500">
              {suggestedClasses.length > 0
                ? `Suggested next: ${suggestedClasses.map((c) => `${c.name}${c.section ? ` ${c.section}` : ''}`).join(', ')}`
                : noSuggestionCopy}
            </p>
          </div>

          {/* Target Class */}
          {mode === 'promote' && (
            <div className="card p-4 space-y-3">
              <h4 className="text-sm font-bold text-gray-900">Target Class</h4>
              {orderedClasses.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No target class available. Create the next class first, or use Complete Highest Class.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {orderedClasses.map((c) => {
                    const cId = c._id || c.id
                    const active = targetClassId === cId
                    const isSuggested = suggestedClasses.some((s) => (s._id || s.id) === cId)
                    return (
                      <button
                        key={cId}
                        onClick={() => setTargetClassId(cId)}
                        className={`px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all text-left ${
                          active
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {c.name}{c.section ? ` ${c.section}` : ''}
                        {isSuggested && (
                          <span className={`block text-xs mt-0.5 ${active ? 'text-green-600' : 'text-primary-500'}`}>
                            Suggested
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {mode === 'complete' && (
            <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
              <GraduationCap className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                Selected students will keep their historical records, and their live class assignment will be cleared for the chosen academic year.
              </p>
            </div>
          )}

          {/* Students */}
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-bold text-gray-900">Students</h4>
                <p className="text-xs text-gray-500">{selectedIds.length} of {students.length} selected</p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setSelectedIds(students.map((s) => getId(s)).filter(Boolean))}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                >
                  Select all
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  className="text-xs font-semibold text-red-500 hover:text-red-600"
                >
                  Clear
                </button>
              </div>
            </div>

            {students.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">
                Add students to this class before using academic progression.
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {students.map((s) => {
                  const sid = getId(s)
                  const isSelected = selectedSet.has(sid)
                  const name = getName(s)
                  return (
                    <button
                      key={sid}
                      onClick={() => toggleStudent(sid)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left ${
                        isSelected ? 'border-primary-300 bg-primary-50' : 'border-gray-100 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <Avatar src={s.avatar} name={name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
                        <p className="text-xs text-gray-500">{s.admissionNo || 'No admission no'}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="btn-secondary" disabled={loading}>Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={loading || !students.length || (mode === 'promote' && !targetClassId)}
            className="btn-primary flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === 'complete' ? 'Complete Class' : 'Promote Students'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function SkeletonSection() {
  return (
    <div className="card p-5 animate-pulse space-y-3">
      <div className="h-4 bg-gray-100 rounded w-1/4" />
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-gray-200" />
        <div className="space-y-2 flex-1">
          <div className="h-3 bg-gray-100 rounded w-1/2" />
          <div className="h-2 bg-gray-100 rounded w-1/3" />
        </div>
      </div>
    </div>
  )
}

export default function ClassDetailPage() {
  const { classId } = useParams()
  const navigate = useNavigate()

  const [allClasses, setAllClasses] = useState([])
  const [students, setStudents] = useState([])
  const [teachers, setTeachers] = useState([])
  const [subjectTeachers, setSubjectTeachers] = useState([])
  const [loading, setLoading] = useState(true)

  const [showCTModal, setShowCTModal] = useState(false)
  const [showTempModal, setShowTempModal] = useState(false)
  const [showPromoModal, setShowPromoModal] = useState(false)
  const [confirmRemoveTemp, setConfirmRemoveTemp] = useState(false)

  const [assigningCT, setAssigningCT] = useState(false)
  const [assigningTemp, setAssigningTemp] = useState(false)
  const [removingTemp, setRemovingTemp] = useState(false)
  const [promoting, setPromoting] = useState(false)

  const [ctSearch, setCtSearch] = useState('')
  const [selectedCTId, setSelectedCTId] = useState('')

  const classInfo = useMemo(
    () => allClasses.find((c) => c._id === classId || c.id === classId) || null,
    [allClasses, classId]
  )

  const rawCT = classInfo?.classTeacher
  const ct = rawCT
    ? typeof rawCT === 'string'
      ? teachers.find((t) => t._id === rawCT || t.id === rawCT) ?? null
      : rawCT
    : null

  const rawTmp = classInfo?.tempClassTeacher
  const tmp = rawTmp?.teacherId
    ? {
        ...rawTmp,
        teacherId: typeof rawTmp.teacherId === 'string'
          ? teachers.find((t) => t._id === rawTmp.teacherId || t.id === rawTmp.teacherId) ?? { _id: rawTmp.teacherId }
          : rawTmp.teacherId,
      }
    : null
  const tempActive = isTempActive(tmp)

  const fetchAll = async () => {
    try {
      setLoading(true)
      const [classData, teacherData, studentData] = await Promise.all([
        classesApi.getAll(),
        teachersApi.getAll(),
        studentsApi.getAll({ classId, limit: 500 }),
      ])
      setAllClasses(Array.isArray(classData) ? classData : classData?.classes ?? [])
      setTeachers(Array.isArray(teacherData) ? teacherData : teacherData?.users ?? teacherData?.teachers ?? [])
      const studs = Array.isArray(studentData) ? studentData : studentData?.users ?? []
      setStudents(studs.filter((s) => s.studentStatus !== 'completed'))
    } catch {
      toast.error('Failed to load class data')
    } finally {
      setLoading(false)
    }
  }

  const fetchSubjectTeachers = async () => {
    try {
      const data = await classesApi.getSubjectTeachers(classId)
      setSubjectTeachers(Array.isArray(data) ? data : [])
    } catch {
      // non-critical, silently ignore
    }
  }

  useEffect(() => {
    fetchAll()
    fetchSubjectTeachers()
  }, [classId])

  const handleAssignCT = async () => {
    if (!selectedCTId) { toast.error('Select a teacher first'); return }
    try {
      setAssigningCT(true)
      await classesApi.assignClassTeacher(classId, { teacherId: selectedCTId })
      await fetchAll()
      toast.success('Class teacher assigned')
      setShowCTModal(false)
      setSelectedCTId('')
      setCtSearch('')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign class teacher')
    } finally {
      setAssigningCT(false)
    }
  }

  const handleAssignTemp = async (teacherId, from, to) => {
    try {
      setAssigningTemp(true)
      await classesApi.assignTempClassTeacher(classId, { teacherId, from, to })
      await fetchAll()
      toast.success('Temporary class teacher assigned')
      setShowTempModal(false)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign temp teacher')
    } finally {
      setAssigningTemp(false)
    }
  }

  const handleRemoveTemp = async () => {
    try {
      setRemovingTemp(true)
      await classesApi.removeTempClassTeacher(classId)
      await fetchAll()
      toast.success('Temporary assignment removed')
      setConfirmRemoveTemp(false)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove temp teacher')
    } finally {
      setRemovingTemp(false)
    }
  }

  const handlePromote = async (payload) => {
    try {
      setPromoting(true)
      await classesApi.promoteStudents(classId, payload)
      await fetchAll()
      setShowPromoModal(false)
      const targetClass = allClasses.find((c) => (c._id || c.id) === payload.targetClassId)
      const targetLabel = targetClass
        ? `${targetClass.name}${targetClass.section ? ` ${targetClass.section}` : ''}`
        : 'the selected class'
      toast.success(
        payload.markCompleted
          ? `${payload.studentIds.length} student(s) completed for ${payload.targetAcademicYear}.`
          : `${payload.studentIds.length} student(s) moved to ${targetLabel} for ${payload.targetAcademicYear}.`
      )
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to promote students')
    } finally {
      setPromoting(false)
    }
  }

  const filteredCTTeachers = useMemo(() => {
    const q = ctSearch.trim().toLowerCase()
    return teachers.filter((t) =>
      !q ||
      getName(t).toLowerCase().includes(q) ||
      (t.email || '').toLowerCase().includes(q) ||
      (t.mobile || '').includes(ctSearch.trim())
    )
  }, [teachers, ctSearch])

  const boys = students.filter((s) => s.gender === 'male').length
  const girls = students.filter((s) => s.gender === 'female').length
  const classLabel = classInfo
    ? `${classInfo.name}${classInfo.section ? ` — ${classInfo.section}` : ''}`
    : 'Class Detail'

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/classes')} className="btn-secondary p-2">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="h-6 bg-gray-200 rounded w-40 animate-pulse" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="card p-6 animate-pulse h-20" />)}
        </div>
        <SkeletonSection />
        <SkeletonSection />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/classes')} className="btn-secondary p-2" title="Back to Classes">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{classLabel}</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {students.length} student{students.length !== 1 ? 's' : ''}
              {(boys || girls) ? ` · ${boys} boys · ${girls} girls` : ''}
            </p>
          </div>
        </div>
        <button onClick={fetchAll} className="btn-secondary p-2" title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Students', value: students.length, color: 'text-primary-600' },
          { label: 'Boys', value: boys, color: 'text-blue-600' },
          { label: 'Girls', value: girls, color: 'text-pink-600' },
        ].map((s) => (
          <div key={s.label} className="card p-4 text-center">
            <p className={`text-2xl font-extrabold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Teachers Card */}
      <div className="card p-5 space-y-5">
        {/* Class Teacher */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center">
              <User className="w-4 h-4 text-primary-600" />
            </div>
            <h3 className="text-sm font-bold text-gray-900">Class Teacher</h3>
          </div>

          {ct ? (
            <div className="flex items-center gap-3">
              <Avatar src={ct.avatar} name={getName(ct)} size="lg" />
              <div className="flex-1 min-w-0">
                <Link
                  to={`/users/${getId(ct)}`}
                  className="text-sm font-semibold text-gray-900 hover:text-primary-700 block truncate"
                >
                  {getName(ct)}
                </Link>
                <p className="text-xs text-gray-500 truncate">{ct.email || ct.mobile || 'No contact info'}</p>
              </div>
              <button
                onClick={() => { setSelectedCTId(String(getId(ct) || '')); setCtSearch(''); setShowCTModal(true) }}
                className="btn-secondary text-xs py-1.5 px-3 flex-shrink-0"
              >
                Change
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setSelectedCTId(''); setCtSearch(''); setShowCTModal(true) }}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-primary-200 bg-primary-50 rounded-xl py-3 text-sm font-semibold text-primary-700 hover:bg-primary-100 transition-colors"
            >
              <Plus className="w-4 h-4" /> Assign Class Teacher
            </button>
          )}
        </div>

        <div className="border-t border-gray-100" />

        {/* Temp Teacher */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${tempActive ? 'bg-amber-50' : 'bg-gray-100'}`}>
              <Clock className={`w-4 h-4 ${tempActive ? 'text-amber-600' : 'text-gray-500'}`} />
            </div>
            <h3 className="text-sm font-bold text-gray-900">Temporary Assignment</h3>
            {tempActive && (
              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">Active</span>
            )}
          </div>

          {tmp?.teacherId ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Avatar src={tmp.teacherId?.avatar} name={getName(tmp.teacherId)} size="lg" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{getName(tmp.teacherId)}</p>
                  <p className="text-xs text-gray-500">{formatDate(tmp.from)} → {formatDate(tmp.to)}</p>
                </div>
                <button
                  onClick={() => setConfirmRemoveTemp(true)}
                  className="border border-red-200 text-red-500 hover:bg-red-50 text-xs py-1.5 px-3 rounded-lg font-semibold transition-colors flex items-center gap-1 flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" /> Remove
                </button>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowTempModal(true)}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                >
                  Reassign temp teacher
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowTempModal(true)}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-amber-200 bg-amber-50 rounded-xl py-3 text-sm font-semibold text-amber-700 hover:bg-amber-100 transition-colors"
            >
              <Plus className="w-4 h-4" /> Assign Temp Teacher (for leave)
            </button>
          )}
        </div>
      </div>

      {/* Subject Teachers */}
      {subjectTeachers.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-green-600" />
            </div>
            <h3 className="text-sm font-bold text-gray-900">Subject Teachers</h3>
          </div>
          <div className="space-y-3">
            {subjectTeachers.map((st, idx) => {
              const subject = st.subject || st
              const teacher = st.teacher
              return (
                <div key={subject?._id || idx} className="flex items-center gap-3">
                  <span className="px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-semibold min-w-[110px] text-center">
                    {subject?.name || 'Subject'}
                  </span>
                  {teacher ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Avatar src={teacher.avatar} name={getName(teacher)} size="xs" />
                      <Link
                        to={`/users/${getId(teacher)}`}
                        className="text-sm text-gray-700 hover:text-primary-700 truncate font-medium"
                      >
                        {getName(teacher)}
                      </Link>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic">Not assigned</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-2 gap-4">
        <button
          onClick={() => setShowPromoModal(true)}
          disabled={!students.length}
          className="card p-4 text-left hover:border-green-300 hover:shadow-md transition-all flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed group"
        >
          <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-100 transition-colors">
            <GitMerge className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">Promote to Next Academic Year</p>
            <p className="text-xs text-gray-500 mt-0.5">Move students to the next class or complete the highest class.</p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </button>

        <Link
          to="/manage-timetable"
          className="card p-4 hover:border-primary-300 hover:shadow-md transition-all flex items-center gap-3 group"
        >
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 transition-colors">
            <Clock className="w-5 h-5 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900">Manage Timetable</p>
            <p className="text-xs text-gray-500 mt-0.5">Open the timetable manager to update class schedules.</p>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </Link>
      </div>

      {/* Student List */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-900">
            Students in {classLabel}
          </h3>
          <Link to="/users" className="text-xs font-semibold text-primary-600 hover:text-primary-700">
            All Users →
          </Link>
        </div>

        {students.length === 0 ? (
          <div className="py-16 text-center">
            <Users className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p className="font-medium text-gray-500">No students in this class</p>
            <p className="text-xs text-gray-400 mt-1">Add students and assign them to this class</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-th">Student</th>
                  <th className="table-th hidden sm:table-cell">Admission No</th>
                  <th className="table-th hidden md:table-cell">Gender</th>
                  <th className="table-th text-right">Profile</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {students.map((s) => {
                  const sid = getId(s)
                  const name = getName(s)
                  const genderColor = s.gender === 'male'
                    ? 'text-blue-600'
                    : s.gender === 'female'
                    ? 'text-pink-600'
                    : 'text-gray-500'
                  return (
                    <tr key={sid} className="hover:bg-gray-50 transition-colors">
                      <td className="table-td">
                        <div className="flex items-center gap-2.5">
                          <Avatar src={s.avatar} name={name} size="sm" />
                          <div>
                            <p className="font-medium text-gray-900">{name}</p>
                            <p className="text-xs text-gray-400">{s.email || '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="table-td hidden sm:table-cell text-gray-500">
                        {s.admissionNo || '—'}
                      </td>
                      <td className="table-td hidden md:table-cell">
                        <span className={`text-xs font-semibold capitalize ${genderColor}`}>
                          {s.gender === 'male' ? '👨 ' : s.gender === 'female' ? '👩 ' : '🧑 '}
                          {s.gender || 'other'}
                        </span>
                      </td>
                      <td className="table-td text-right">
                        <Link
                          to={`/users/${sid}`}
                          className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                        >
                          View →
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Class Teacher Assign Modal */}
      {showCTModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Assign Class Teacher</h3>
              <button onClick={() => setShowCTModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <Search className="w-4 h-4 text-gray-400" />
                <input
                  value={ctSearch}
                  onChange={(e) => setCtSearch(e.target.value)}
                  placeholder="Search teacher..."
                  className="bg-transparent text-sm outline-none flex-1"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredCTTeachers.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-10">No teachers found</p>
              ) : filteredCTTeachers.map((t) => {
                const tid = String(getId(t))
                const isSelected = selectedCTId === tid
                return (
                  <button
                    key={tid}
                    onClick={() => setSelectedCTId(tid)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                      isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <Avatar src={t.avatar} name={getName(t)} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{getName(t)}</p>
                      <p className="text-xs text-gray-500 truncate">{t.email || t.mobile || 'No contact'}</p>
                    </div>
                    {isSelected && <UserCheck className="w-4 h-4 text-primary-600 flex-shrink-0" />}
                  </button>
                )
              })}
            </div>
            <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-100">
              <button onClick={() => setShowCTModal(false)} className="btn-secondary flex-1" disabled={assigningCT}>
                Cancel
              </button>
              <button
                onClick={handleAssignCT}
                disabled={assigningCT || !selectedCTId}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {assigningCT && <Loader2 className="w-4 h-4 animate-spin" />}
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Temp Teacher Modal */}
      {showTempModal && (
        <TempTeacherModal
          teachers={teachers}
          onClose={() => setShowTempModal(false)}
          onConfirm={handleAssignTemp}
          loading={assigningTemp}
        />
      )}

      {/* Remove Temp Confirm */}
      {confirmRemoveTemp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <X className="w-5 h-5 text-red-500" />
            </div>
            <h3 className="font-bold text-gray-900 mb-1">Remove Temp Teacher?</h3>
            <p className="text-sm text-gray-500 mb-5">
              Are you sure you want to remove the temporary class teacher assignment?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmRemoveTemp(false)} className="btn-secondary flex-1" disabled={removingTemp}>
                Cancel
              </button>
              <button
                onClick={handleRemoveTemp}
                disabled={removingTemp}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {removingTemp && <Loader2 className="w-4 h-4 animate-spin" />}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promotion Modal */}
      {showPromoModal && classInfo && (
        <PromotionModal
          currentClass={classInfo}
          allClasses={allClasses}
          students={students}
          onClose={() => setShowPromoModal(false)}
          onConfirm={handlePromote}
          loading={promoting}
        />
      )}
    </div>
  )
}
